// ** React Imports
import { createContext, useEffect, useState, ReactNode } from 'react';

// ** Next Import
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

// ** Axios
import axios, { AxiosError } from 'axios';

import { client } from 'src/utils/axios-client';
// ** Config
import authConfig from 'src/configs/auth';

// ** Types
import { AuthValuesType, RegisterParams, LoginParams, UserDataType } from './types';
import { ErrorCallbackType } from 'src/types';
import { debug } from 'console';
import useApplication from '../v2/hooks/useApplication.hooks';

// ** Defaults
const defaultProvider: AuthValuesType = {
    user: null,
    loading: true,
    setUser: () => null,
    setLoading: () => Boolean,
    isInitialized: false,
    login: () => Promise.resolve(),
    logout: () => Promise.resolve(),
    setIsInitialized: () => Boolean,
    register: () => Promise.resolve(),
};

const AuthContext = createContext(defaultProvider);

type Props = {
    children: ReactNode;
};

const AuthProvider = ({ children }: Props) => {
    // ** States
    const [user, setUser] = useState<UserDataType | null>(defaultProvider.user);
    const [loading, setLoading] = useState<boolean>(defaultProvider.loading);
    const [isInitialized, setIsInitialized] = useState<boolean>(defaultProvider.isInitialized);
    const { setIsLoading } = useApplication();

    // ** Hooks
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const initAuth = async (): Promise<void> => {
            setIsInitialized(true);

            setLoading(true);
            await client
                .get(authConfig.meEndpoint)
                .then(async (response) => {
                    setLoading(false);

                    if (axios.isAxiosError(response)) {
                        setUser(null);
                        setLoading(false);
                    } else {
                        setUser({ ...response.data });
                        setLoading(false);

                        redirect();
                    }
                })
                .catch(() => {
                    setUser(null);
                    setLoading(false);
                });
        };
        initAuth();
    }, []);

    const redirect = () => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(urlSearchParams.entries());
        
        const redirectURL = params.returnUrl && params.returnUrl !== '/' ? params.returnUrl : '/';
        navigate(redirectURL as string);
    };

    const handleLogin = (params: LoginParams, errorCallback?: ErrorCallbackType) => {
        client
            .post(authConfig.loginEndpoint, params)
            .then(async (res) => {
                setIsLoading(false);

                if (axios.isAxiosError(res)) {
                    setUser(null);
                    setLoading(false);

                    const error = res as unknown as AxiosError;
                    const errors = (error.response as any).data.errors;

                    if (errorCallback) errorCallback(errors);
                    throw new Error(errors);
                }
            })
            .then(() => {
                client.get(authConfig.meEndpoint).then(async (response) => {
                    setIsLoading(false);
                    setLoading(false);

                    if (axios.isAxiosError(response)) {
                        setUser(null);
                        setLoading(false);

                        const error = response as unknown as AxiosError;
                        const errors = (error.response as any).data.errors;

                        if (errorCallback) errorCallback(errors);
                    } else {
                        setUser({ ...response.data });
                        redirect();
                    }
                });
            })
            .catch((err) => {
                if (err.errors) {
                    if (errorCallback) errorCallback(err);
                }

                throw err;
            });
    };

    const handleLogout = async () => {
        await client.get(authConfig.logoutEndpoint).then(async (response) => {
            setUser(null);
            setIsInitialized(false);
            navigate('/login');
        });
    };

    const handleRegister = (params: RegisterParams, errorCallback?: ErrorCallbackType) => {
        client
            .post(authConfig.registerEndpoint, params)
            .then((res) => {
                if (axios.isAxiosError(res)) {
                    const error = res as unknown as AxiosError;
                    const errors = (error.response as any).data.errors;

                    if (errorCallback) errorCallback(errors);
                } else {
                    handleLogin({ email: params.email, password: params.password });
                }
            })
            .catch((err) => (errorCallback ? errorCallback(err.response.data) : null));
    };

    const values = {
        user,
        loading,
        setUser,
        setLoading,
        isInitialized,
        setIsInitialized,
        login: handleLogin,
        logout: handleLogout,
        register: handleRegister,
    };

    return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };
